/**
 * DungeonPanel.jsx - Dungeon Exploration with Pixi.js
 *
 * Uses the shared Pixi app service (same WebGL context as WorldMapPanel)
 * to render dungeon hex maps without context corruption.
 * UI layout matches WorldMapPanel exactly.
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import * as PIXI from 'pixi.js';
import { useGame } from '../../context/GameContext';
import { coordsToId, getAdjacentCoords, hexToPixel, getHexPoints } from '../../utils/hexGrid';
import { DUNGEON_CONFIG, TILE_TYPES } from '../../data/dungeon';
import { ENCOUNTER_TILE_TYPES } from '../../modules/dungeon/generation';
import { getDefaultPoolsForTier } from '../../data/world-map/dungeonBiomes';
import { ENCOUNTER_BIOMES, ENCOUNTER_CONFIG } from '../../data/world-map/encounterBiomes';
import { TOWER_BIOMES } from '../../data/world-map/towerBiomes';
import CombatModal from '../modals/CombatModal';
import {
  getPixiApp,
  getDungeonContainer,
  showDungeonScene,
  showWorldMapScene,
  clearDungeonScene,
  isReady,
} from '../../services/PixiAppService';
import './DungeonPanel.css';

const HEX_SIZE = 40;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2.0;
const GRID_SIZE = 25;
const DPR = Math.min(window.devicePixelRatio || 1, 2);
const TEXTURE_SCALE = DPR * 2;

// Player movement animation state - matches WorldMapPanel exactly
let playerMoveAnimation = {
  isAnimating: false,
  startPos: { x: 0, y: 0 },
  targetPos: { x: 0, y: 0 },
  progress: 0,
  duration: 0.45, // 450ms - matches WorldMapPanel
  lastTileId: null, // Track last known tile ID
  landingPulse: 0, // Pulse effect when landing (0-1)
  landingSquash: 0, // Squash on landing (0-1)
};

// Player icon pool (persists across renders)
let playerIconPool = {
  icon: null,
  ring: null,
  outer: null,
  mid: null,
  inner: null,
};

// Create grid texture for dungeon (more visible than WorldMap)
function createGridTexture(gridSize, lineColor, lineAlpha) {
  const canvas = document.createElement('canvas');
  const scaledGridSize = gridSize * TEXTURE_SCALE;
  const tileSize = scaledGridSize * 20;
  canvas.width = tileSize;
  canvas.height = tileSize;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.strokeStyle = lineColor;
  ctx.globalAlpha = lineAlpha;
  ctx.lineWidth = TEXTURE_SCALE;
  for (let x = 0; x <= canvas.width; x += scaledGridSize) {
    ctx.beginPath();
    ctx.moveTo(x + 0.5, 0);
    ctx.lineTo(x + 0.5, canvas.height);
    ctx.stroke();
  }
  for (let y = 0; y <= canvas.height; y += scaledGridSize) {
    ctx.beginPath();
    ctx.moveTo(0, y + 0.5);
    ctx.lineTo(canvas.width, y + 0.5);
    ctx.stroke();
  }
  const texture = PIXI.Texture.from(canvas);
  texture.source.resolution = TEXTURE_SCALE;
  return texture;
}

// Dungeon vignette - darker and more intense for cave atmosphere
function createDungeonVignetteTexture(size) {
  const canvas = document.createElement('canvas');
  const scaledSize = size * TEXTURE_SCALE;
  canvas.width = scaledSize;
  canvas.height = scaledSize;
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createRadialGradient(scaledSize/2, scaledSize/2, 0, scaledSize/2, scaledSize/2, scaledSize/2);
  // Darker, more intense vignette for dungeon cave feel
  gradient.addColorStop(0, 'rgba(20,18,16,0)');      // Clear center
  gradient.addColorStop(0.35, 'rgba(18,16,14,0)');   // Still clear
  gradient.addColorStop(0.55, 'rgba(15,13,11,0.15)'); // Start darkening earlier
  gradient.addColorStop(0.75, 'rgba(10,9,8,0.4)');   // Stronger mid-darkness
  gradient.addColorStop(0.9, 'rgba(5,4,3,0.65)');    // Very dark edges
  gradient.addColorStop(1, 'rgba(0,0,0,0.8)');       // Near-black corners
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, scaledSize, scaledSize);
  const texture = PIXI.Texture.from(canvas);
  texture.source.resolution = TEXTURE_SCALE;
  return texture;
}

// Boost color saturation for more vibrant tiles (same as WorldMap)
function boostSaturation(color, factor = 1.4) {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const gray = (r + g + b) / 3;
  const newR = Math.min(255, Math.round(gray + (r - gray) * factor));
  const newG = Math.min(255, Math.round(gray + (g - gray) * factor));
  const newB = Math.min(255, Math.round(gray + (b - gray) * factor));
  return (newR << 16) | (newG << 8) | newB;
}

// Get tile color based on type - matches WorldMapPanel style
function getTileColor(type, isCurrentNode, isAdjacent, isCleared) {
  // Cleared tiles are dimmer gray
  if (isCleared && !isCurrentNode) {
    return 0x2a2a2a; // Dim gray for cleared tiles
  }

  // Tile colors - keep type color for all tiles
  const colors = {
    [TILE_TYPES.ENTRANCE]: 0x4a5058,   // Stone blue-gray
    [TILE_TYPES.BOSS]: 0x5a3838,       // Dark crimson
    [TILE_TYPES.TREASURE]: 0x3a4838,   // Forest green
    [TILE_TYPES.REST]: 0x4a4232,       // Warm amber
    [TILE_TYPES.TRAP]: 0x3a3848,       // Purple-gray (swapped from combat)
    [TILE_TYPES.COMBAT]: 0x4a3232,     // Dark red (swapped from trap)
    [TILE_TYPES.EMPTY]: 0x383838,      // Neutral gray
    [ENCOUNTER_TILE_TYPES.EXIT]: 0x3a5838, // Bright green for exit (victory!)
    // Tower-specific tiles
    [TILE_TYPES.STAIRS_DOWN]: 0x3a4858,   // Blue-teal for stairs down
    [TILE_TYPES.STAIRS_UP]: 0x584a3a,     // Warm brown for stairs up
    [TILE_TYPES.CHECKPOINT]: 0x3a5848,    // Green-teal for checkpoint
  };

  let baseColor = colors[type] || 0x383838;

  if (isCurrentNode) {
    // Prominent gold-brown for player tile (matches WorldMap: 0x5a5550 boosted)
    return boostSaturation(0x5a5550, 1.5);
  }

  // Adjacent tiles keep their type color (with gold border overlay)
  return boostSaturation(baseColor, 1.2);
}

// Get tile border color - matches WorldMapPanel style
function getTileBorderColor(type, isCurrentNode, isAdjacent) {
  // Current node: bright gold border (matches WorldMap: 0xffd480)
  if (isCurrentNode) return 0xffd480;

  // Adjacent tiles: use type-based border (no gold highlight)
  // Other tiles: muted border
  switch (type) {
    case TILE_TYPES.ENTRANCE: return 0x506080;
    case TILE_TYPES.BOSS: return 0x804040;
    case TILE_TYPES.TREASURE: return 0x508030;
    case TILE_TYPES.REST: return 0x806030;
    case TILE_TYPES.TRAP: return 0x0088ff;     // Blue border for traps
    case TILE_TYPES.COMBAT: return 0xff0000;   // Red border for combat
    case ENCOUNTER_TILE_TYPES.EXIT: return 0x50ff50;  // Bright green border for exit
    // Tower-specific tiles
    case TILE_TYPES.STAIRS_DOWN: return 0x4080b0;   // Cyan border for stairs down
    case TILE_TYPES.STAIRS_UP: return 0xb08040;     // Gold-brown border for stairs up
    case TILE_TYPES.CHECKPOINT: return 0x40b060;    // Green border for checkpoint
    default: return 0x5a5448; // Matches WorldMap default
  }
}

// Get tile icon based on type
function getTileIcon(type, isCleared) {
  // Tiles that should ALWAYS keep their icon (never show checkmark)
  const persistentIcons = {
    [TILE_TYPES.ENTRANCE]: '🚪',
    [TILE_TYPES.REST]: '🔥',
    [ENCOUNTER_TILE_TYPES.EXIT]: '🏁',
    // Tower-specific tiles that persist
    [TILE_TYPES.STAIRS_DOWN]: '⬇️',
    [TILE_TYPES.STAIRS_UP]: '⬆️',
    [TILE_TYPES.CHECKPOINT]: '🏳️',
  };

  // Check if this tile type should keep its icon
  if (persistentIcons[type]) {
    return persistentIcons[type];
  }

  // Cleared tiles (combat, treasure, trap, boss) show checkmark
  if (isCleared) {
    return '✓';
  }

  const icons = {
    [TILE_TYPES.BOSS]: '👑',
    [TILE_TYPES.TREASURE]: '💎',
    [TILE_TYPES.COMBAT]: '⚔️',
    [TILE_TYPES.TRAP]: '💀',
    [TILE_TYPES.EMPTY]: '·',
  };
  return icons[type] || '·';
}

// Create layered hex with inner highlights for depth (like WorldMap)
function createLayeredHex(size, baseColor, borderColor, borderWidth = 1, isHighlighted = false) {
  const g = new PIXI.Graphics();
  const lighterColor = ((baseColor & 0xfefefe) >> 1) + 0x505050;
  const pts = getHexPoints(size);

  // Base fill
  g.poly(pts);
  g.fill({ color: baseColor });

  // Inner highlight layer
  g.poly(getHexPoints(size - 4));
  g.fill({ color: lighterColor, alpha: 0.15 });

  // Center shine
  g.poly(getHexPoints(size - 8));
  g.fill({ color: 0xffffff, alpha: 0.08 });

  // Border with smooth joins
  g.poly(pts);
  g.stroke({ color: borderColor, width: borderWidth, alpha: 1, join: 'round' });

  return g;
}

const DungeonPanel = () => {
  const {
    currentDungeon,
    moveInDungeon,
    enterDungeon,
    exitDungeon,
    completeDungeon,
    playerCharacter,
    currentEnemy,
    dungeonLoot,
    getPlayerStats,
    moveToFloor,
    useCheckpoint,
  } = useGame();

  const [zoom, setZoom] = useState(1.0);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [messages, setMessages] = useState([]);
  const [pixiReady, setPixiReady] = useState(false);
  const [showResultPopup, setShowResultPopup] = useState(null); // { type: 'complete' | 'death', stats }
  const [dungeonStats, setDungeonStats] = useState({ mobsKilled: 0, bossName: null, bossDrops: [] });

  const containerRef = useRef(null);
  const worldContainerRef = useRef(null);
  const bgLayerRef = useRef(null);
  const tileGraphicsRef = useRef(new Map());
  const gridTextureRef = useRef(null);
  const vignetteTextureRef = useRef(null);
  const uiLayerRef = useRef(null);
  const playerLayerRef = useRef(null);
  const tickerCallbackRef = useRef(null);

  // Show toast message
  const showMessage = useCallback((text, isError = false) => {
    const id = Date.now() + Math.random();
    setMessages(prev => [...prev, { id, text, isError }]);
    setTimeout(() => {
      setMessages(prev => prev.filter(m => m.id !== id));
    }, 1500);
  }, []);

  // Track dungeon ID to detect when we enter a NEW dungeon (not just move within one)
  const currentDungeonIdRef = useRef(null);

  // Initialize Pixi scene when dungeon is active
  // IMPORTANT: Only run on dungeon ENTER/EXIT, not on every move!
  useEffect(() => {
    // Only initialize if we have a dungeon and haven't set up yet
    const dungeonId = currentDungeon ? `${currentDungeon.tierId}-${currentDungeon.seed}` : null;

    // Skip if no dungeon
    if (!currentDungeon || !containerRef.current) {
      // If we had a dungeon and now we don't, clean up happens via return function
      return;
    }

    // If already initialized for this dungeon AND containers still exist, don't re-run
    if (currentDungeonIdRef.current === dungeonId &&
        worldContainerRef.current &&
        bgLayerRef.current &&
        uiLayerRef.current) {
      return;
    }

    // Check if WorldMap's Pixi app is ready
    if (!isReady()) {
      console.warn('[DungeonPanel] Pixi app not ready - WorldMap must initialize first');
      return;
    }

    const app = getPixiApp();
    if (!app) return;

    // Mark that we're initialized for this dungeon
    currentDungeonIdRef.current = dungeonId;

    // Switch to dungeon scene
    showDungeonScene();

    // Get or create dungeon container
    const dungeonScene = getDungeonContainer();
    if (!dungeonScene) return;

    // Attach canvas to our container
    if (!containerRef.current.contains(app.canvas)) {
      containerRef.current.appendChild(app.canvas);
    }

    // Resize to fit container
    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;
    app.renderer.resize(width, height);

    // Change background to darker gray for dungeon atmosphere
    app.renderer.background.color = 0x252320; // Dark gray-brown for cave feel

    // Create background layer with grid (like WorldMap but more visible)
    if (!bgLayerRef.current) {
      const bgLayer = new PIXI.Container();
      bgLayer.zIndex = -2;
      dungeonScene.addChild(bgLayer);
      bgLayerRef.current = bgLayer;

      // Create grid texture - lighter color for more visibility against dark bg
      if (!gridTextureRef.current) {
        gridTextureRef.current = createGridTexture(GRID_SIZE, '#4a4540', 0.18);
      }

      // Grid tiling sprite (more visible for dungeon)
      const gridSprite = new PIXI.TilingSprite({
        texture: gridTextureRef.current,
        width: 4000,
        height: 4000
      });
      gridSprite.position.set(-2000, -2000);
      gridSprite.alpha = 0.6; // More visible than WorldMap's 0.5
      bgLayer.addChild(gridSprite);
    }

    // Create UI layer for vignette (on top of everything)
    if (!uiLayerRef.current) {
      const uiLayer = new PIXI.Container();
      uiLayer.zIndex = 100; // Above all dungeon content
      dungeonScene.addChild(uiLayer);
      uiLayerRef.current = uiLayer;

      // Create vignette texture
      if (!vignetteTextureRef.current) {
        vignetteTextureRef.current = createDungeonVignetteTexture(512);
      }

      // Add vignette sprite for cave atmosphere
      const vignette = new PIXI.Sprite(vignetteTextureRef.current);
      vignette.width = width * 1.5;
      vignette.height = height * 1.5;
      vignette.anchor.set(0.5);
      vignette.position.set(width / 2, height / 2);
      vignette.label = 'vignette';
      uiLayer.addChild(vignette);
    }

    // Create world container for panning/zooming
    if (!worldContainerRef.current) {
      const worldContainer = new PIXI.Container();
      worldContainer.label = 'DungeonWorld';
      worldContainer.sortableChildren = true;
      worldContainer.zIndex = 0;
      dungeonScene.addChild(worldContainer);
      worldContainerRef.current = worldContainer;

      // Set initial camera position (same as WorldMapPanel)
      worldContainer.scale.set(zoom);
      worldContainer.position.set(
        app.screen.width / 2 + panOffset.x * zoom,
        app.screen.height / 2 + panOffset.y * zoom
      );
    }

    // Create player layer (on top of tiles, below UI)
    if (!playerLayerRef.current) {
      const playerLayer = new PIXI.Container();
      playerLayer.label = 'PlayerLayer';
      playerLayer.zIndex = 50; // Above tiles, below vignette
      worldContainerRef.current.addChild(playerLayer);
      playerLayerRef.current = playerLayer;

      // Create player icon elements (matches WorldMapPanel)
      const TEXT_RESOLUTION = 2;

      // Outer glow ring
      const outer = new PIXI.Graphics();
      outer.circle(0, 0, HEX_SIZE * 0.75);
      outer.fill({ color: 0xffd060, alpha: 0.12 });
      playerLayer.addChild(outer);
      playerIconPool.outer = outer;

      // Mid glow ring
      const mid = new PIXI.Graphics();
      mid.circle(0, 0, HEX_SIZE * 0.55);
      mid.fill({ color: 0xffd060, alpha: 0.18 });
      playerLayer.addChild(mid);
      playerIconPool.mid = mid;

      // Inner glow ring
      const inner = new PIXI.Graphics();
      inner.circle(0, 0, HEX_SIZE * 0.35);
      inner.fill({ color: 0xffd060, alpha: 0.25 });
      playerLayer.addChild(inner);
      playerIconPool.inner = inner;

      // Gold ring
      const ring = new PIXI.Graphics();
      ring.circle(0, 0, HEX_SIZE * 0.85);
      ring.stroke({ color: 0xffd060, width: 2, alpha: 0.35 });
      playerLayer.addChild(ring);
      playerIconPool.ring = ring;

      // Crown emoji icon (matches WorldMapPanel: 👑)
      const icon = new PIXI.Text({
        resolution: TEXT_RESOLUTION,
        text: '👑',
        style: {
          fontFamily: 'serif',
          fontSize: HEX_SIZE * 0.6,
          fontWeight: 'normal',
        }
      });
      icon.anchor.set(0.5);
      playerLayer.addChild(icon);
      playerIconPool.icon = icon;

      // Reset animation state for new dungeon
      playerMoveAnimation.lastTileId = null;
      playerMoveAnimation.isAnimating = false;
      playerMoveAnimation.landingPulse = 0;
      playerMoveAnimation.landingSquash = 0;
    }

    // Add ticker callback for animations
    if (!tickerCallbackRef.current) {
      const tickerCallback = (ticker) => {
        if (!playerLayerRef.current || !playerIconPool.icon) return;

        const phase = Date.now() / 1000;

        // Handle movement animation
        if (playerMoveAnimation.isAnimating) {
          playerMoveAnimation.progress += ticker.deltaTime / 60 / playerMoveAnimation.duration;

          if (playerMoveAnimation.progress >= 1) {
            playerMoveAnimation.isAnimating = false;
            playerMoveAnimation.progress = 1;
            playerMoveAnimation.landingPulse = 1.0;
            playerMoveAnimation.landingSquash = 1.0;
          }

          const t = playerMoveAnimation.progress;
          // Ease out cubic for smooth deceleration
          const easedT = 1 - Math.pow(1 - t, 3);

          // Hop arc - parabola for vertical offset
          const hopHeight = HEX_SIZE * 0.6;
          const hopArc = Math.sin(t * Math.PI) * hopHeight;

          // Interpolate position
          const currentX = playerMoveAnimation.startPos.x + (playerMoveAnimation.targetPos.x - playerMoveAnimation.startPos.x) * easedT;
          const currentY = playerMoveAnimation.startPos.y + (playerMoveAnimation.targetPos.y - playerMoveAnimation.startPos.y) * easedT - hopArc;

          // Update all player elements
          playerIconPool.outer.position.set(currentX, currentY);
          playerIconPool.mid.position.set(currentX, currentY);
          playerIconPool.inner.position.set(currentX, currentY);
          playerIconPool.ring.position.set(currentX, currentY);
          playerIconPool.icon.position.set(currentX, currentY - HEX_SIZE * 0.1);
        }

        // Landing pulse effect
        if (playerMoveAnimation.landingPulse > 0) {
          playerMoveAnimation.landingPulse = Math.max(0, playerMoveAnimation.landingPulse - ticker.deltaTime / 60 * 5);
          const pulseScale = 1 + playerMoveAnimation.landingPulse * 0.5;
          playerIconPool.ring.scale.set(pulseScale);
          playerIconPool.ring.alpha = 0.35 * (1 + playerMoveAnimation.landingPulse * 1.0);
        }

        // Landing squash/bounce effect
        if (playerMoveAnimation.landingSquash > 0) {
          playerMoveAnimation.landingSquash = Math.max(0, playerMoveAnimation.landingSquash - ticker.deltaTime / 60 * 4);
          const bounceHeight = Math.sin(playerMoveAnimation.landingSquash * Math.PI) * HEX_SIZE * 0.15;
          const baseY = playerMoveAnimation.targetPos.y - HEX_SIZE * 0.1;
          playerIconPool.icon.position.y = baseY - bounceHeight;

          // Squash effect on icon
          const squash = playerMoveAnimation.landingSquash * 0.3;
          playerIconPool.icon.scale.set(1 + squash * 0.3, 1 - squash * 0.2);
        }

        // Breathing effect when idle (not animating)
        if (!playerMoveAnimation.isAnimating && playerMoveAnimation.landingSquash <= 0) {
          const breathe = 1 + Math.sin(phase * 1.8) * 0.03;
          playerIconPool.icon.scale.set(breathe);

          // Subtle glow pulse
          const glowPulse = 0.12 + Math.sin(phase * 2.5) * 0.03;
          playerIconPool.outer.alpha = glowPulse;
        }
      };

      app.ticker.add(tickerCallback);
      tickerCallbackRef.current = tickerCallback;
    }

    // Start ticker
    app.ticker.start();
    setPixiReady(true);

    return () => {
      // Remove ticker callback
      if (tickerCallbackRef.current && app && app.ticker) {
        app.ticker.remove(tickerCallbackRef.current);
        tickerCallbackRef.current = null;
      }

      // Clean up player layer
      if (playerLayerRef.current) {
        playerLayerRef.current.destroy({ children: true });
        playerLayerRef.current = null;
      }
      playerIconPool = { icon: null, ring: null, outer: null, mid: null, inner: null };

      // Clean up dungeon-specific layers
      if (bgLayerRef.current) {
        bgLayerRef.current.destroy({ children: true });
        bgLayerRef.current = null;
      }
      if (uiLayerRef.current) {
        uiLayerRef.current.destroy({ children: true });
        uiLayerRef.current = null;
      }
      if (worldContainerRef.current) {
        worldContainerRef.current.destroy({ children: true });
        worldContainerRef.current = null;
      }
      tileGraphicsRef.current.clear();
      setPixiReady(false);
      currentDungeonIdRef.current = null;

      // Restore WorldMap's background color
      if (app && app.renderer && !app.renderer.destroyed) {
        app.renderer.background.color = 0x4a4238; // WorldMap's original color
      }

      // Switch back to world map scene
      showWorldMapScene();
    };
  }, [currentDungeon?.tierId, currentDungeon?.seed]); // Only depend on dungeon identity

  // Render dungeon tiles
  useEffect(() => {
    if (!pixiReady || !currentDungeon || !worldContainerRef.current) return;

    const app = getPixiApp();
    if (!app) return;

    const worldContainer = worldContainerRef.current;
    const tiles = currentDungeon.tiles;
    const playerPos = currentDungeon.playerPosition;
    const playerTile = tiles[playerPos];

    // Calculate adjacent tiles
    const adjacentTileIds = new Set();
    if (playerTile && !playerCharacter.isInCombat) {
      const adjacentCoords = getAdjacentCoords(playerTile.q, playerTile.r);
      adjacentCoords.forEach(({ q, r }) => {
        const adjId = coordsToId(q, r);
        if (tiles[adjId]?.discovered) {
          adjacentTileIds.add(adjId);
        }
      });
    }

    // Clear old tile graphics (but preserve player layer)
    tileGraphicsRef.current.forEach((container) => {
      worldContainer.removeChild(container);
      container.destroy({ children: true });
    });
    tileGraphicsRef.current.clear();

    // Draw each tile
    Object.values(tiles).forEach(tile => {
      const { q, r, type, discovered, visited } = tile;
      const tileId = coordsToId(q, r);
      const pos = hexToPixel(q, r, HEX_SIZE);
      const isCurrentNode = tileId === playerPos;
      const isAdjacent = adjacentTileIds.has(tileId);
      const isCleared = visited && type !== TILE_TYPES.ENTRANCE; // Visited tiles are cleared

      const tileContainer = new PIXI.Container();
      tileContainer.position.set(pos.x, pos.y);
      tileContainer.zIndex = isCurrentNode ? 10 : 1;

      if (!discovered) {
        // Fog tile - dark mysterious cave look
        const fog = new PIXI.Graphics();
        const fogPts = getHexPoints(HEX_SIZE - 1);
        fog.poly(fogPts);
        fog.fill({ color: 0x1a1816, alpha: 0.95 }); // Very dark for cave
        fog.stroke({ width: 1, color: 0x2a2624, alpha: 0.4, join: 'round' });
        tileContainer.addChild(fog);

        // Question mark - subtle
        const text = new PIXI.Text({
          text: '?',
          style: {
            fontFamily: 'Cinzel, serif',
            fontSize: 16,
            fontWeight: 'bold',
            fill: 0x454038,
          }
        });
        text.anchor.set(0.5);
        text.alpha = 0.4;
        tileContainer.addChild(text);
      } else {
        // Discovered tile - use layered hex like WorldMap
        const fillColor = getTileColor(type, isCurrentNode, isAdjacent, isCleared);
        const borderColor = getTileBorderColor(type, isCurrentNode, isAdjacent);
        // Current node: 2.5 width, adjacent: 1 with alpha, others: 1
        const borderWidth = isCurrentNode ? 2.5 : 1;

        const hex = createLayeredHex(HEX_SIZE - 1, fillColor, borderColor, borderWidth, isCurrentNode);
        hex.label = 'tileHex';
        tileContainer.addChild(hex);

        // No gold highlighting for adjacent tiles - just make them interactive
        // Player gold ring is now rendered in the separate animated player layer

        // Icon - cleared tiles show checkmark, others show type icon
        const icon = getTileIcon(type, isCleared);
        const iconText = new PIXI.Text({
          text: icon,
          style: {
            fontFamily: isCleared ? 'Arial, sans-serif' : 'Arial, sans-serif',
            fontSize: isCleared ? 16 : 18,
            fontWeight: isCleared ? 'bold' : 'normal',
            fill: isCleared ? 0x6a8a5a : 0xffffff, // Green checkmark for cleared
          }
        });
        iconText.anchor.set(0.5);
        iconText.alpha = isCleared ? 0.6 : 1; // Dim checkmark
        tileContainer.addChild(iconText);

        // Make adjacent tiles interactive (double-click to move)
        if (isAdjacent && !playerCharacter.isInCombat) {
          tileContainer.eventMode = 'static';
          tileContainer.cursor = 'pointer';

          // Double-click detection
          let lastClickTime = 0;
          tileContainer.on('pointerdown', () => {
            const now = Date.now();
            const timeSinceLastClick = now - lastClickTime;

            if (timeSinceLastClick < 300) { // 300ms double-click threshold
              // Double-click detected
              const result = moveInDungeon(tileId);
              if (result.success) {
                showMessage('Moved');
              } else {
                showMessage(result.error || 'Cannot move there', true);
              }
              lastClickTime = 0; // Reset to prevent triple-click
            } else {
              // First click
              lastClickTime = now;
            }
          });

          // Hover effect - add white border on hover
          tileContainer.on('pointerover', () => {
            const hoverBorder = new PIXI.Graphics();
            hoverBorder.poly(getHexPoints(HEX_SIZE - 1));
            hoverBorder.stroke({ color: 0xffffff, width: 2, alpha: 0.6, join: 'round' });
            hoverBorder.label = 'hoverBorder';
            tileContainer.addChild(hoverBorder);
          });
          tileContainer.on('pointerout', () => {
            const hoverBorder = tileContainer.children.find(c => c.label === 'hoverBorder');
            if (hoverBorder) {
              tileContainer.removeChild(hoverBorder);
              hoverBorder.destroy();
            }
          });
        }
      }

      worldContainer.addChild(tileContainer);
      tileGraphicsRef.current.set(tileId, tileContainer);
    });

  }, [pixiReady, currentDungeon?.playerPosition, currentDungeon?.tiles, playerCharacter.isInCombat, moveInDungeon, showMessage]);

  // Update player icon position and trigger movement animation
  useEffect(() => {
    if (!pixiReady || !currentDungeon || !playerIconPool.icon) return;

    const playerPos = currentDungeon.playerPosition;
    const playerTile = currentDungeon.tiles[playerPos];
    if (!playerTile) return;

    const targetPos = hexToPixel(playerTile.q, playerTile.r, HEX_SIZE);

    // Check if player moved to a new tile
    if (playerMoveAnimation.lastTileId !== null && playerMoveAnimation.lastTileId !== playerPos) {
      // Player moved - start animation from last position
      const lastTile = currentDungeon.tiles[playerMoveAnimation.lastTileId];
      if (lastTile) {
        const startPos = hexToPixel(lastTile.q, lastTile.r, HEX_SIZE);
        playerMoveAnimation.isAnimating = true;
        playerMoveAnimation.startPos = { x: startPos.x, y: startPos.y };
        playerMoveAnimation.targetPos = { x: targetPos.x, y: targetPos.y };
        playerMoveAnimation.progress = 0;
        playerMoveAnimation.landingPulse = 0;
        playerMoveAnimation.landingSquash = 0;
      }
    } else if (!playerMoveAnimation.isAnimating) {
      // Not animating - snap to position (initial placement or after animation)
      playerIconPool.outer.position.set(targetPos.x, targetPos.y);
      playerIconPool.mid.position.set(targetPos.x, targetPos.y);
      playerIconPool.inner.position.set(targetPos.x, targetPos.y);
      playerIconPool.ring.position.set(targetPos.x, targetPos.y);
      playerIconPool.icon.position.set(targetPos.x, targetPos.y - HEX_SIZE * 0.1);
    }

    // Update last known tile ID
    playerMoveAnimation.lastTileId = playerPos;
    // Store target for landing animation
    playerMoveAnimation.targetPos = { x: targetPos.x, y: targetPos.y };

  }, [pixiReady, currentDungeon?.playerPosition]);

  // Separate effect for camera position (zoom/pan) - no tile re-rendering
  // Matches WorldMapPanel exactly: position = screen center + panOffset * zoom
  useEffect(() => {
    const app = getPixiApp();
    const wc = worldContainerRef.current;
    if (!app || !wc || !pixiReady) return;

    // Exactly matches WorldMapPanel line 700:
    // wc.position.set(app.screen.width / 2 + panOffset.x * zoom, app.screen.height / 2 + panOffset.y * zoom);
    wc.scale.set(zoom);
    wc.position.set(
      app.screen.width / 2 + panOffset.x * zoom,
      app.screen.height / 2 + panOffset.y * zoom
    );

    // Also move background grid with the camera
    const bgLayer = bgLayerRef.current;
    if (bgLayer) {
      bgLayer.position.set(panOffset.x * zoom, panOffset.y * zoom);
      bgLayer.scale.set(zoom);
    }
  }, [zoom, panOffset, pixiReady]); // Only zoom, pan, and pixiReady - no currentDungeon!

  // Use refs to store latest zoom/panOffset for resize handler
  const zoomRef = useRef(zoom);
  const panOffsetRef = useRef(panOffset);
  useEffect(() => {
    zoomRef.current = zoom;
    panOffsetRef.current = panOffset;
  }, [zoom, panOffset]);

  // Handle window resize - matches WorldMapPanel pattern
  useEffect(() => {
    if (!pixiReady || !containerRef.current) return;

    const handleResize = () => {
      const app = getPixiApp();
      if (!app?.renderer || !containerRef.current) return;

      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;

      // Resize renderer
      app.renderer.resize(width, height);

      // Update vignette position/size
      const uiLayer = uiLayerRef.current;
      if (uiLayer) {
        const vignette = uiLayer.children.find(c => c.label === 'vignette');
        if (vignette) {
          vignette.width = width * 1.5;
          vignette.height = height * 1.5;
          vignette.position.set(width / 2, height / 2);
        }
      }

      // Update camera position using ref values
      const wc = worldContainerRef.current;
      if (wc) {
        wc.position.set(
          app.screen.width / 2 + panOffsetRef.current.x * zoomRef.current,
          app.screen.height / 2 + panOffsetRef.current.y * zoomRef.current
        );
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [pixiReady]); // Only depend on pixiReady, use refs for zoom/panOffset

  // Mouse handlers for pan
  const handleMouseDown = useCallback((e) => {
    if (e.button === 0) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
    }
  }, [panOffset]);

  const handleMouseMove = useCallback((e) => {
    if (!isPanning) return;
    setPanOffset({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
  }, [isPanning, panStart]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Wheel zoom
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setZoom(prev => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev + (e.deltaY > 0 ? -0.1 : 0.1))));
  }, []);

  // Handle exit
  const handleExit = () => {
    // For towers on floor 1 without checkpoint, warn about losing loot
    if (currentDungeon?.isTower) {
      const isFloorOne = currentDungeon.floor === 1;
      const hasCheckpoint = currentDungeon.checkpointVisited;

      if (isFloorOne && !hasCheckpoint && dungeonLoot.length > 0) {
        // Confirm exit - will lose all loot
        if (!window.confirm(`Exit tower on Floor 1 without checkpoint?\n\nYou will LOSE all ${dungeonLoot.length} items!\n\nVisit a checkpoint (🏳️) first to secure your loot.`)) {
          return;
        }
      }
    }

    clearDungeonScene();
    const result = exitDungeon();
    if (result.success) {
      if (currentDungeon?.isTower) {
        showMessage(`Exited tower! Collected ${result.loot.length} items`);
      } else {
        showMessage(`Exited! Collected ${result.loot.length} items`);
      }
    }
  };

  // Center on player
  const centerOnPlayer = useCallback(() => {
    setPanOffset({ x: 0, y: 0 });
  }, []);

  // Track mobs killed - detect when combat ends with enemy dead
  const prevEnemyRef = useRef(null);
  useEffect(() => {
    // If we had an enemy and now we don't, and we're not in combat, enemy was killed
    if (prevEnemyRef.current && !currentEnemy && !playerCharacter.isInCombat && currentDungeon) {
      const wasKilled = prevEnemyRef.current;
      setDungeonStats(prev => ({
        ...prev,
        mobsKilled: prev.mobsKilled + 1,
        bossName: wasKilled.isBoss ? wasKilled.name : prev.bossName,
        bossDrops: wasKilled.isBoss ? dungeonLoot.slice(-3) : prev.bossDrops, // Last 3 items from boss
      }));
    }
    prevEnemyRef.current = currentEnemy;
  }, [currentEnemy, playerCharacter.isInCombat, currentDungeon, dungeonLoot]);

  // Death detection - show popup instead of immediate exit
  useEffect(() => {
    if (currentDungeon && playerCharacter.currentHealth <= 0) {
      clearDungeonScene();
      const tierData = DUNGEON_CONFIG.TIERS.find(t => t.id === currentDungeon.tierId);
      const fullLoot = [...dungeonLoot];
      const halfCount = Math.floor(fullLoot.length / 2);
      const keptLoot = fullLoot.slice(0, halfCount); // First half kept on death
      setShowResultPopup({
        type: 'death',
        tierName: tierData?.name || 'Dungeon',
        tierIcon: tierData?.icon || '🏚️',
        loot: keptLoot,           // Only show the loot they actually get
        totalLoot: fullLoot.length, // Show how many they found total
        lostLoot: fullLoot.length - halfCount, // Show how many they lost
        mobsKilled: dungeonStats.mobsKilled,
      });
      // Don't exit yet - let user close popup
    }
  }, [currentDungeon, playerCharacter.currentHealth, dungeonLoot, dungeonStats.mobsKilled]);

  // Dungeon selection screen when not in dungeon
  if (!currentDungeon) {
    return (
      <div className="dungeon-selection-screen">
        <div className="dungeon-selection-header">
          <h2 className="dungeon-selection-title">⚔️ Dungeon Selection</h2>
          <p className="dungeon-selection-subtitle">Choose your challenge</p>
        </div>

        <div className="dungeon-tier-list">
          {DUNGEON_CONFIG.TIERS.map((tier) => {
            const isLocked = false; // TODO: Check era requirements
            return (
              <div
                key={tier.id}
                className={`dungeon-tier-card ${isLocked ? 'locked' : ''}`}
              >
                <div className="tier-icon">{tier.icon}</div>
                <div className="tier-info">
                  <h3 className="tier-name">{tier.name}</h3>
                  <div className="tier-details">
                    <span className="tier-detail">⚔️ Lv.{tier.baseEnemyLevel}+</span>
                    <span className="tier-detail">💎 {tier.lootTable}</span>
                    <span className="tier-detail">✨ {tier.expReward} EXP</span>
                  </div>
                </div>
                <button
                  className="tier-enter-btn"
                  onClick={() => {
                    const pools = getDefaultPoolsForTier(tier.id);
                    const result = enterDungeon(tier.id, pools.mobPool, pools.bossPool);
                    if (result.success) {
                      showMessage(`Entering ${tier.name}...`);
                    }
                  }}
                  disabled={isLocked}
                >
                  {isLocked ? '🔒 Locked' : '➡️ Enter'}
                </button>
              </div>
            );
          })}
        </div>

        <div className="dungeon-selection-footer">
          <p>Defeat enemies to collect loot. Survive until you defeat the boss!</p>
          <p className="footer-warning">⚠️ Death means losing the run (keep collected loot)</p>
        </div>
      </div>
    );
  }

  const tierData = DUNGEON_CONFIG.TIERS.find(t => t.id === currentDungeon.tierId);

  // For encounters, get biome info instead of tier data
  const isEncounter = currentDungeon?.isEncounter;
  const encounterBiome = isEncounter ? ENCOUNTER_BIOMES[currentDungeon.encounterBiomeId] : null;

  // For towers, get tower biome info
  const isTower = currentDungeon?.isTower;
  const towerBiome = isTower ? TOWER_BIOMES[currentDungeon.towerBiomeId] : null;

  // Get current tile type for interaction UI
  const currentTileId = currentDungeon?.playerPosition;
  const currentTile = currentTileId ? currentDungeon.tiles[currentTileId] : null;
  const currentTileType = currentTile?.type;

  // Calculate EXP reward for encounters (based on player level)
  // Formula: BASE_XP + (playerLevel * LEVEL_MULTIPLIER) = ~40 XP at level 1, scaling up
  const encounterExpReward = isEncounter
    ? Math.floor(ENCOUNTER_CONFIG.COMPLETION_BASE_XP + (currentDungeon.playerLevel || 1) * ENCOUNTER_CONFIG.COMPLETION_LEVEL_MULTIPLIER)
    : 0;

  return (
    <div className="wm-layout">
      <div className="wm-map-area">
        {/* Toast messages */}
        <div className="wm-toast-stack">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`wm-toast ${msg.isError ? 'error' : 'success'}`}
            >
              {msg.text}
            </div>
          ))}
        </div>

        {/* Dungeon/Encounter/Tower name badge - top left */}
        <div className="wm-explore-row">
          <div className="wm-explore-limit dungeon-badge">
            <span className="wm-explore-icon">
              {isTower ? (towerBiome?.icon || '🗼') : isEncounter ? (encounterBiome?.icon || '⛺') : (tierData?.icon || '🏚️')}
            </span>
            <span className="wm-explore-text">
              {isTower ? (towerBiome?.name || 'Tower') : isEncounter ? (encounterBiome?.name || 'Encounter') : (tierData?.name || 'Dungeon')}
            </span>
          </div>
          {/* Floor indicator for towers */}
          {isTower && (
            <div className="wm-explore-limit tower-floor-badge">
              <span className="wm-explore-text">Floor {currentDungeon.floor}/{currentDungeon.maxFloors}</span>
            </div>
          )}
        </div>

        {/* Map controls - top right (identical to WorldMap) */}
        <div className="wm-map-controls">
          <button onClick={() => setZoom(prev => Math.min(MAX_ZOOM, prev + 0.2))}>+</button>
          <button onClick={() => setZoom(prev => Math.max(MIN_ZOOM, prev - 0.2))}>−</button>
          <button onClick={centerOnPlayer}>⌖</button>
          <span>{Math.round(zoom * 100)}%</span>
        </div>

        {/* Canvas container - exactly like WorldMap */}
        <div
          ref={containerRef}
          className="wm-canvas-wrap"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => setIsPanning(false)}
          onWheel={handleWheel}
        />

        {/* Exit button - bottom left */}
        <button className={`dungeon-exit-btn ${isTower ? 'tower' : ''}`} onClick={handleExit}>
          🚪 Exit {isTower ? 'Tower' : isEncounter ? 'Encounter' : 'Dungeon'}
        </button>

        {/* Hint text - bottom center */}
        <div className="wm-map-hint">
          Drag to pan • Scroll to zoom • Click tiles to move
        </div>
      </div>

      {/* Side panel - clean redesigned UI */}
      <div className="dungeon-side-panel">
        {/* Header Card - Dungeon/Encounter/Tower info + Player HP */}
        <div className="dungeon-header-card">
          <div className="dungeon-header-top">
            <span className="dungeon-tier-icon">
              {isTower ? (towerBiome?.icon || '🗼') : isEncounter ? (encounterBiome?.icon || '⛺') : (tierData?.icon || '🏚️')}
            </span>
            <div className="dungeon-tier-info">
              <h3>{isTower ? (towerBiome?.name || 'Tower') : isEncounter ? (encounterBiome?.name || 'Encounter') : (tierData?.name || 'Dungeon')}</h3>
              <span className="dungeon-status">
                {isTower
                  ? (currentDungeon.bossDefeated ? '✅ Tower Conquered' : `🗼 Floor ${currentDungeon.floor}/${currentDungeon.maxFloors}`)
                  : isEncounter
                    ? (currentDungeon.completed ? '✅ Exit Found' : '🚪 Find Exit')
                    : (currentDungeon.bossDefeated ? '✅ Boss Defeated' : '👑 Boss Awaits')
                }
              </span>
            </div>
          </div>
          <div className="dungeon-player-hp">
            <div className="hp-label">
              <span>❤️ HP</span>
              <span>{playerCharacter.currentHealth} / {getPlayerStats?.()?.health || 100}</span>
            </div>
            <div className="dungeon-hp-bar-container player">
              <div
                className="dungeon-hp-bar-fill player"
                style={{ width: `${(playerCharacter.currentHealth / (getPlayerStats?.()?.health || 100)) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Tower Floor Indicator with progress pips */}
        {isTower && (
          <div className="tower-floor-indicator">
            <div className="floor-progress-label">
              <span>🗼 Tower Progress</span>
              <span className="floor-current">Floor {currentDungeon.floor} of {currentDungeon.maxFloors}</span>
            </div>
            <div className="floor-progress-pips">
              {Array.from({ length: currentDungeon.maxFloors }, (_, i) => {
                const floorNum = i + 1;
                const isCurrentFloor = floorNum === currentDungeon.floor;
                const isVisited = currentDungeon.floorStates && currentDungeon.floorStates[floorNum];
                const isBossFloor = floorNum === currentDungeon.maxFloors;
                return (
                  <div
                    key={floorNum}
                    className={`floor-pip ${isCurrentFloor ? 'current' : ''} ${isVisited ? 'visited' : ''} ${isBossFloor ? 'boss' : ''}`}
                    title={`Floor ${floorNum}${isBossFloor ? ' (Boss)' : ''}${isVisited ? ' - Visited' : ''}`}
                  >
                    {isBossFloor ? '👑' : floorNum}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Stair Interaction UI - shows when standing on stairs */}
        {isTower && !playerCharacter.isInCombat && (currentTileType === TILE_TYPES.STAIRS_DOWN || currentTileType === TILE_TYPES.STAIRS_UP) && (
          <div className="tower-stair-section">
            <div className="section-header">🚶 Stairs</div>
            <div className="stair-buttons">
              {/* STAIRS_UP = ascend to next higher floor */}
              {currentTileType === TILE_TYPES.STAIRS_UP && currentDungeon.floor < currentDungeon.maxFloors && (
                <button
                  className="stair-btn up"
                  onClick={() => {
                    const result = moveToFloor('up');
                    if (result?.success) {
                      showMessage(`Ascended to Floor ${currentDungeon.floor + 1}`);
                    } else {
                      showMessage(result?.error || 'Cannot ascend', true);
                    }
                  }}
                >
                  ⬆️ Ascend to Floor {currentDungeon.floor + 1}
                </button>
              )}
              {/* STAIRS_DOWN = descend to previous lower floor */}
              {currentTileType === TILE_TYPES.STAIRS_DOWN && currentDungeon.floor > 1 && (
                <button
                  className="stair-btn down"
                  onClick={() => {
                    const result = moveToFloor('down');
                    if (result?.success) {
                      showMessage(`Descended to Floor ${currentDungeon.floor - 1}`);
                    } else {
                      showMessage(result?.error || 'Cannot descend', true);
                    }
                  }}
                >
                  ⬇️ Descend to Floor {currentDungeon.floor - 1}
                </button>
              )}
            </div>
            <p className="stair-hint">Moving between floors heals 20% HP (once per transition)</p>
          </div>
        )}

        {/* Checkpoint Interaction UI - shows when standing on checkpoint */}
        {isTower && !playerCharacter.isInCombat && currentTileType === TILE_TYPES.CHECKPOINT && (
          <div className="tower-checkpoint-section">
            <div className="section-header">🏳️ Checkpoint</div>
            <div className="checkpoint-buttons">
              <button
                className="checkpoint-btn rest"
                onClick={() => {
                  const result = useCheckpoint('rest');
                  if (result?.success) {
                    showMessage(`Rested! Healed ${result.healedAmount} HP`);
                  } else {
                    showMessage(result?.error || 'Cannot rest', true);
                  }
                }}
              >
                🔥 Rest (+50% HP)
              </button>
              <button
                className="checkpoint-btn exit"
                onClick={() => {
                  clearDungeonScene();
                  const result = useCheckpoint('exit');
                  if (result?.success) {
                    showMessage(`Exited tower with ${dungeonLoot.length} items`);
                  }
                }}
              >
                🚪 Exit Tower
              </button>
            </div>
            <p className="checkpoint-hint">Rest heals you • Exit keeps all loot</p>
          </div>
        )}

        {/* Loot Section */}
        <div className="dungeon-loot-section">
          <div className="section-header">💎 Loot ({dungeonLoot.length})</div>
          {dungeonLoot.length > 0 ? (
            <div className="dungeon-loot-list">
              {dungeonLoot.slice(-4).map((item, i) => (
                <div key={i} className={`dungeon-loot-item ${item.rarity}`}>
                  <span className="loot-icon">{item.icon}</span>
                  <span className="loot-name">{item.name}</span>
                </div>
              ))}
              {dungeonLoot.length > 4 && (
                <div className="dungeon-loot-more">+{dungeonLoot.length - 4} more...</div>
              )}
            </div>
          ) : (
            <div className="loot-empty">Defeat enemies to collect loot</div>
          )}
        </div>

        {/* Complete Button - shows when boss defeated (dungeons) or exit found (encounters) */}
        {((currentDungeon.bossDefeated && !isEncounter) || (isEncounter && currentDungeon.completed)) && !playerCharacter.isInCombat && (
          <button
            className="dungeon-complete-btn"
            onClick={() => {
              const completionExp = isEncounter ? encounterExpReward : (tierData?.expReward || 50);
              const mobExp = currentDungeon.expEarned || 0;
              const totalExp = completionExp + mobExp;
              clearDungeonScene();
              setShowResultPopup({
                type: 'complete',
                tierName: isEncounter ? (encounterBiome?.name || 'Encounter') : (tierData?.name || 'Dungeon'),
                tierIcon: isEncounter ? (encounterBiome?.icon || '⛺') : (tierData?.icon || '🏚️'),
                expReward: completionExp,
                mobExp: mobExp,
                totalExp: totalExp,
                loot: [...dungeonLoot],
                mobsKilled: dungeonStats.mobsKilled,
                bossName: isEncounter ? null : dungeonStats.bossName,
                bossDrops: isEncounter ? [] : dungeonStats.bossDrops,
                isEncounter,
              });
            }}
          >
            🏆 Complete (+{(isEncounter ? encounterExpReward : (tierData?.expReward || 0)) + (currentDungeon.expEarned || 0)} EXP)
          </button>
        )}

        {/* Stats Footer */}
        <div className="dungeon-stats-footer">
          <div className="stat-item">
            <span className="stat-label">Kills</span>
            <span className="stat-value">{dungeonStats.mobsKilled}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">EXP</span>
            <span className="stat-value">{currentDungeon.expEarned || 0}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Loot</span>
            <span className="stat-value">{dungeonLoot.length}</span>
          </div>
        </div>
      </div>

      {/* Combat Modal - Full screen overlay when in combat */}
      {playerCharacter.isInCombat && currentEnemy && (
        <CombatModal
          onVictory={() => {
            // Victory handled by combat tick in GameContext
            // Modal auto-closes when currentEnemy becomes null
          }}
        />
      )}

      {/* Result Popup (Completion or Death) */}
      {showResultPopup && (
        <div className="dungeon-result-overlay">
          <div className={`dungeon-result-popup ${showResultPopup.type}`}>
            {/* Header */}
            <div className="result-header">
              <span className="result-icon">
                {showResultPopup.type === 'complete' ? '🏆' : '💀'}
              </span>
              <h2 className="result-title">
                {showResultPopup.type === 'complete' ? 'Dungeon Complete!' : 'Brutally Injured!'}
              </h2>
              <p className="result-subtitle">
                {showResultPopup.type === 'complete'
                  ? `${showResultPopup.tierIcon} ${showResultPopup.tierName} Conquered`
                  : 'You have been returned to your kingdom'
                }
              </p>
            </div>

            {/* Stats */}
            <div className="result-stats">
              {showResultPopup.type === 'complete' && (
                <div className="result-stat exp">
                  <span className="stat-icon">✨</span>
                  <span className="stat-value">+{showResultPopup.totalExp || showResultPopup.expReward}</span>
                  <span className="stat-label">Total EXP</span>
                  {showResultPopup.mobExp > 0 && (
                    <span className="stat-breakdown">
                      ({showResultPopup.mobExp} mobs + {showResultPopup.expReward} bonus)
                    </span>
                  )}
                </div>
              )}
              <div className="result-stat">
                <span className="stat-icon">⚔️</span>
                <span className="stat-value">{showResultPopup.mobsKilled}</span>
                <span className="stat-label">Mobs Killed</span>
              </div>
              <div className="result-stat">
                <span className="stat-icon">💎</span>
                <span className="stat-value">{showResultPopup.loot.length}</span>
                <span className="stat-label">{showResultPopup.type === 'death' ? 'Items Kept' : 'Items Found'}</span>
              </div>
              {showResultPopup.type === 'death' && showResultPopup.lostLoot > 0 && (
                <div className="result-stat penalty">
                  <span className="stat-icon">💀</span>
                  <span className="stat-value">-{showResultPopup.lostLoot}</span>
                  <span className="stat-label">Items Lost</span>
                </div>
              )}
            </div>

            {/* Boss Info (if completed) */}
            {showResultPopup.type === 'complete' && showResultPopup.bossName && (
              <div className="result-boss">
                <h3>👑 Boss Defeated: {showResultPopup.bossName}</h3>
                {showResultPopup.bossDrops.length > 0 && (
                  <div className="boss-drops">
                    {showResultPopup.bossDrops.map((item, i) => (
                      <div key={i} className={`drop-item ${item.rarity}`}>
                        <span>{item.icon}</span>
                        <span>{item.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Loot Preview */}
            {showResultPopup.loot.length > 0 && (
              <div className="result-loot">
                <h3>📦 Loot Collected</h3>
                <div className="loot-grid">
                  {showResultPopup.loot.slice(0, 8).map((item, i) => (
                    <div key={i} className={`loot-item ${item.rarity}`}>
                      <span className="loot-icon">{item.icon}</span>
                      <span className="loot-name">{item.name}</span>
                    </div>
                  ))}
                  {showResultPopup.loot.length > 8 && (
                    <div className="loot-more">+{showResultPopup.loot.length - 8} more...</div>
                  )}
                </div>
              </div>
            )}

            {/* Close Button */}
            <button
              className="result-close-btn"
              onClick={() => {
                if (showResultPopup.type === 'complete') {
                  completeDungeon();
                } else {
                  // Pass isDeath=true to only get half loot
                  exitDungeon(true);
                }
                setShowResultPopup(null);
                setDungeonStats({ mobsKilled: 0, bossName: null, bossDrops: [] });
              }}
            >
              {showResultPopup.type === 'complete' ? '🏠 Return to Kingdom' : '💔 Accept Defeat'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DungeonPanel;
